/**
 * Export Utilities for Customers
 * Provides functionality to export customer data to Excel format
 */

import { type CustomerUI } from "@/types/ui/customers";
import { formatDateDDMMYYYY, formatCurrency } from "@/lib/utils";

/**
 * Export customers to Excel (CSV format)
 * @param customers - Array of customers to export
 * @param filename - Optional filename (defaults to customers-{date}.csv)
 * @param visibleColumns - Optional array of column IDs to export (exports all if not provided)
 */
export function exportCustomersToExcel(
  customers: CustomerUI[],
  filename?: string,
  visibleColumns?: string[]
): void {
  if (customers.length === 0) {
    throw new Error("No customers to export");
  }

  // Define all available columns
  const allColumns = [
    { key: "accountNumber", label: "Account Number" },
    { key: "name", label: "Customer Name" },
    { key: "contact", label: "Contact Email" },
    { key: "currency", label: "Currency" },
    { key: "outstanding", label: "Outstanding" },
    { key: "overdue", label: "Overdue" },
    { key: "creditLimit", label: "Credit Limit" },
    { key: "dso", label: "DSO (Days)" },
    { key: "risk", label: "Risk Level" },
    { key: "lastPayment", label: "Last Payment" },
    { key: "phone", label: "Phone" },
    { key: "address", label: "Address" },
    { key: "paymentTerms", label: "Payment Terms" },
    { key: "collectionStrategyId", label: "Collection Strategy" },
    { key: "creditControllerId", label: "Credit Controller" },
    { key: "accountManagerId", label: "Account Manager" },
    { key: "availableCredit", label: "Available Credit" },
    { key: "creditUtilization", label: "Credit Utilization %" },
  ];

  // Filter columns if visibleColumns is provided
  const columnsToExport = visibleColumns
    ? allColumns.filter((col) => visibleColumns.includes(col.key))
    : allColumns;

  // Create CSV header
  const header = columnsToExport.map((col) => `"${col.label}"`).join(",");

  // Create CSV rows
  const rows = customers.map((customer) => {
    return columnsToExport
      .map((col) => {
        const key = col.key;
        let value: string | number = "";

        // Get value based on key
        switch (key) {
          case "accountNumber":
            value = customer.accountNumber;
            break;
          case "name":
            value = customer.name;
            break;
          case "contact":
            value = customer.contact;
            break;
          case "currency":
            value = customer.currency;
            break;
          case "outstanding":
            value = formatCurrency(customer.outstanding, customer.currency);
            break;
          case "overdue":
            value = formatCurrency(customer.overdue, customer.currency);
            break;
          case "creditLimit":
            value = formatCurrency(customer.creditLimit, customer.currency);
            break;
          case "dso":
            value = customer.dso.toString();
            break;
          case "risk":
            value = customer.risk.charAt(0).toUpperCase() + customer.risk.slice(1);
            break;
          case "lastPayment":
            value = formatDateDDMMYYYY(customer.lastPayment);
            break;
          case "phone":
            value = customer.phone;
            break;
          case "address":
            value = customer.address;
            break;
          case "paymentTerms":
            value = customer.paymentTerms;
            break;
          case "collectionStrategyId":
            value = customer.collectionStrategyId || "None";
            break;
          case "creditControllerId":
            value = customer.creditControllerId || "None";
            break;
          case "accountManagerId":
            value = customer.accountManagerId || "None";
            break;
          case "availableCredit":
            value = formatCurrency(
              customer.creditLimit - customer.outstanding,
              customer.currency
            );
            break;
          case "creditUtilization":
            value =
              customer.creditLimit > 0
                ? ((customer.outstanding / customer.creditLimit) * 100).toFixed(1) + "%"
                : "0.0%";
            break;
          default:
            value = "";
        }

        // Escape quotes and wrap in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(",");
  });

  // Combine header and rows
  const csv = [header, ...rows].join("\n");

  // Create blob and download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    filename || `customers-${new Date().toISOString().split("T")[0]}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Calculate totals for selected customers
 */
export interface CustomerTotals {
  count: number;
  totalOutstanding: number;
  totalOverdue: number;
  currency: string;
}

export function calculateCustomerTotals(customers: CustomerUI[]): CustomerTotals {
  if (customers.length === 0) {
    return {
      count: 0,
      totalOutstanding: 0,
      totalOverdue: 0,
      currency: "GBP",
    };
  }

  const totalOutstanding = customers.reduce((sum, c) => sum + c.outstanding, 0);
  const totalOverdue = customers.reduce((sum, c) => sum + c.overdue, 0);

  // Use the currency from the first customer
  const currency = customers[0].currency;

  return {
    count: customers.length,
    totalOutstanding,
    totalOverdue,
    currency,
  };
}
